import { Avatar, Flex, useToast } from '@chakra-ui/core'
import { IComment, RootState } from 'constants/interfaces'
import useAvatar from 'hooks/useAvatar'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ChatMessage } from './ChatMessage'
import useComments from 'hooks/useComments'
import { Params, useParams } from 'react-router-dom'
import { getCommentActivity, setCommentsCounter } from 'redux/actions/comments'
import { Post } from 'redux/reducers/feed'
import keys from 'constants/keys'

interface IChatItemProps {
  message: IComment
  currentChat: Post
  onMessageClick?: (commentId: string) => void
  isTarget?: boolean
}
export const ChatItem = ({ message, currentChat, onMessageClick, isTarget = false }: IChatItemProps) => {
  const itemRef = useRef<HTMLDivElement | null>(null)
  const { id } = useParams<Params<string>>()
  const dispatch = useDispatch()
  const toast = useToast()
  const avatar = useAvatar(message.created_by)
  const { uid } = useSelector((state: RootState) => state.auth)
  const { isFetching } = useSelector((state: RootState) => state.comments)
  const { commentsCounter, deleteComment } = useComments(id || currentChat.post_id)
  const {
    fetchCommentsCounter: fetchReliesCounter,
    commentsCounter: repliesCounter,
    commentsCounterLoading: repliesCounterLoading,
  } = useComments(message.cid, true)

  const replies = useSelector((state: RootState) => state.comments.repliesState?.[message.cid]?.items || [])
  const isUserMessage = message.created_by === uid

  const deleteCommentHandler = async (cid: string) => {
    if (window.confirm('Are you sure ?')) {
      try {
        deleteComment({ id: id || currentChat.post_id, cid: cid, isReplyed: false })
        toast({
          title: 'Comment deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })

        dispatch(
          setCommentsCounter({
            count: commentsCounter - (replies.length || 0) - 1,
            key: id || currentChat.post_id,
            isReply: false,
          })
        )
      } catch (error) {
        toast({
          title: 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }
  }

  const onChatClick = (commentId: string) => {
    if (
      currentChat.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT &&
      message.object_type !== keys.COMMENT_OBJECT_TYPE.COMMENT_ON_ANNOUNCEMENT_GROUP_MESSAGE
    ) {
      // Если передан обработчик из модального окна, используем его
      if (onMessageClick) {
        onMessageClick(commentId)
        return
      }
    }
  }

  useEffect(() => {
    if (currentChat.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT && !isFetching && !message.isDummy) {
      dispatch(getCommentActivity(message.cid))
      fetchReliesCounter(message.cid, id || currentChat.post_id)
    }
  }, [currentChat.type, dispatch, fetchReliesCounter, id, currentChat.post_id, isFetching, message])

  useEffect(() => {
    if (!isTarget) return
    const timeout = window.setTimeout(() => {
      itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 120)

    return () => window.clearTimeout(timeout)
  }, [isTarget])

  return (
    <Flex
      ref={itemRef}
      flexDirection={isUserMessage ? 'row-reverse' : 'row'}
      justifyContent="space-between"
      mx="10px"
      mb="30px"
      h="max-content"
      pos="relative"
    >
      <Flex maxW={'70%'} flexDir={isUserMessage ? 'row-reverse' : 'row'} style={{ columnGap: '15px' }}>
        <Avatar w="50px" h="50px" src={avatar} />
        <ChatMessage
          onChatClick={onChatClick}
          message={message}
          isUserMessage={isUserMessage}
          deleteCommentHandler={deleteCommentHandler}
          currentChat={currentChat}
          commentsCounter={repliesCounter}
          commentsCounterLoading={repliesCounterLoading}
        />
      </Flex>
    </Flex>
  )
}
